'use client'

import { Link } from '@/components/ui/link'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/css-utils'
import { Mutation } from '@/lib/generated/graphql-types'
import {
  STEP_QUERY_PARAM,
  REVIEW_PANE_QUERY_PARAM,
} from '@/modules/onboarding/constants'
import { OnboardingPageIds } from '@/modules/surveys/configs/onboarding/constants'
import {
  SALARY_ID_KEY,
  SALARY_SUBMISSION_SURVEY_ID_KEY,
} from '@/modules/user/profile/constants'
import { EDIT_SALARY } from '@/modules/user/profile/queries'
import { MainRoutes } from '@/utils/routes'
import { useMutation } from '@apollo/client'
import { ToastAction } from '@radix-ui/react-toast'
import posthog from 'posthog-js'
import { Loader2, Pencil } from 'lucide-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

export function EditReviewLink({
  pageId,
  className,
  onClick,
}: {
  pageId: OnboardingPageIds
  className?: string
  onClick?: () => void
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [url, setUrl] = useState('')
  const isEditSalary = pathname.startsWith(MainRoutes.ProfileSalariesEdit)
  const salaryId = searchParams.get(SALARY_ID_KEY)
  const { push } = useRouter()
  const { toast } = useToast()

  const [editSalary, { loading: isLoadingSalary }] =
    useMutation<Pick<Mutation, 'editSalary'>>(EDIT_SALARY)

  useEffect(() => {
    const decoratedOnboardingPageHref = new URL(
      pathname,
      window.location.origin,
    )
    searchParams.forEach((value, key) => {
      decoratedOnboardingPageHref.searchParams.set(key, value)
    })

    decoratedOnboardingPageHref.searchParams.set(
      REVIEW_PANE_QUERY_PARAM,
      'true',
    )
    decoratedOnboardingPageHref.searchParams.set(STEP_QUERY_PARAM, pageId)
    setUrl(decoratedOnboardingPageHref.toString())
  }, [pageId, pathname, searchParams, searchParams.entries])

  const handleClick = async (evt: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isEditSalary) {
      onClick?.()
      return
    }
    evt.preventDefault()
    if (isLoadingSalary) return

    const { data } = await editSalary({
      variables: {
        salaryId,
      },
    })
    const submissionId = data?.editSalary?.submissionId
    const urlObj = new URL(url)
    if (salaryId) {
      urlObj.searchParams.set(SALARY_ID_KEY, salaryId)
    }
    if (submissionId) {
      urlObj.searchParams.set(SALARY_SUBMISSION_SURVEY_ID_KEY, submissionId)
    } else {
      toast({
        title: 'Error',
        description: 'Failed to edit salary',
        action: (
          <ToastAction
            altText="OK"
            className="bg-button-primary-bg text-button-primary-text"
          >
            OK
          </ToastAction>
        ),
      })
      posthog.captureException('Failed to edit salary', {
        salaryId,
        submissionId,
        url,
      })
    }

    push(urlObj.toString())
    onClick?.()
  }

  if (typeof window === 'undefined') return null

  return (
    <Link
      onClick={handleClick}
      href={url}
      className={cn('flex items-center gap-2', className)}
      variant="outlineButton"
      size="sm"
    >
      {isLoadingSalary ? (
        <Loader2 className="w-4 h-4 text-copy-muted animate-spin" />
      ) : (
        <Pencil className="w-4 h-4 text-copy-muted" />
      )}
      Edit
    </Link>
  )
}
